import {
  approveRequest,
  getRequestDetail,
  rejectRequest,
  RequestDetailsForApprovalResponse,
  sendBackRequest,
  signAttestationEmail,
  cancelRequest,
  resubmitMyRequest,
} from "@api";
import { request } from "@api/request";
import { faFileAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";
import { State } from "@store";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { AttestationModal } from "../../../request/steps/summary/views/summary/attestation-modal/attestation-modal";
import { Modal } from "../../modal";
import { ApproversDetails } from "./approversDetails";
import { Buttons } from "./buttons";
import { RequestDetails } from "./requestDetails";
import { ModalHistoryUserRequest } from "./requestHistoryModal";
import { RequestorDetails } from "./requestorDetails";
import { alertService } from "@services";
import { Button } from "@material-ui/core";
import { Modal as ComponentModal } from "@components";

const useStyles = makeStyles({
  link: {
    fontFamily: "Arial",
    color: "#0f7dca",
    cursor: "pointer",
  },
});

export interface Props {
  encryptId?: string;
}

export const Details: React.FC<Props> = ({ encryptId }) => {
  const [detail, setDetail] = useState<RequestDetailsForApprovalResponse>(
    {} as RequestDetailsForApprovalResponse
  );
  const user = useSelector((state: State) => state.user);
  const [current_user_name] = useSelector((state: State) => [
    state.user?.name || "",
    state.user?.email || "",
  ]);
  const [openHistory, setOpenHistory] = useState<boolean>(false);
  const [openSendBack, setOpenSendBack] = useState<boolean>(false);
  const [openApprove, setOpenApprove] = useState<boolean>(false);
  const [openReject, setOpenReject] = useState<boolean>(false);
  const [openCancel, setOpenCancel] = useState<boolean>(false);
  const [openAttestation, setOpenAttestation] = useState<boolean>(false);
  const [attestation, setAttestation] = useState<string>("");
  const [isMobileAttestation, setIsAttestationMobile] = useState<boolean>(
    false
  );
  const [attestationError, setAttestationError] = useState<Error | undefined>(
    undefined
  );
  const history = useHistory();
  const location = useLocation<{
    readonlyAttestation?: boolean;
    prevPage?: string;
  }>();
  const classNames = useStyles();
  const params: { encryptId: string } = useParams();

  useEffect(() => {
    (async () => {
      try {
        const details = await getRequestDetail(
          !!encryptId ? encryptId : params.encryptId
        );
        setIsAttestationMobile(details.request.isMobileAttestation);
        setDetail(details);
        if (details.request.isAttestationSigned) {
          setAttestation(details.request.attestationName);
        }
      } catch (err) {
        console.log(err);
      }
    })();
  }, [encryptId, isMobileAttestation, params.encryptId]);

  const onAttestationAccept = (accept: boolean) => {
    if (attestation !== current_user_name) {
      setAttestationError(new Error(`Value should be "${current_user_name}"`));
      return;
    }
    setIsAttesting(true);
    signAttestationEmail(detail.request.id, accept, attestation).then(() =>
      setDetail({
        ...detail,
        request: { ...detail.request, isAttestationNeeded: false },
      })
    );
    setIsAttesting(false);
    setOpenAttestation(false);
  };

  const onCloseAttestationModal = () => {
    setOpenAttestation(false);
  };

  const onSendBack = useCallback(
    async (comments: string) => {
      const success = await sendBackRequest(detail.request?.id || 0, comments);
      if (success) {
        history.push(`/dashboard/approve-request`);
        alertService.success({
          msg: "Request has been successfully sent back to the requestor.",
        });
      } else {
        alertService.danger({ msg: "Failed to send back." });
      }
      return success
        ? { success }
        : { success, error: "There was an error Sending Back" };
    },
    [detail.request]
  );

  const onReject = useCallback(
    async (comments: string) => {
      const success = await rejectRequest(detail.request?.id, comments);
      if (success) {
        history.push(`/dashboard/approve-request`);
        alertService.success({
          msg: "Request has been successfully rejected.",
        });
      } else {
        alertService.danger({ msg: "Failed to reject." });
      }
      return success
        ? { success }
        : { success, error: "There was an error Rejecting" };
    },
    [detail.request]
  );

  const onCancel = useCallback(
    async (comments: string) => {
      const success = await cancelRequest(detail.request?.id, comments);
      if (success) {
        history.goBack();
        alertService.success({
          msg: "Request has been successfully cancelled.",
        });
      } else {
        alertService.danger({ msg: "Failed to cancel." });
      }
      return success
        ? { success }
        : { success, error: "There was an error Cancelling" };
    },
    [detail.request]
  );

  const onApproveRequest = useCallback(
    async (comments: string) => {
      let success = false;
      if (
        detail.request?.status === "CANCELLED" ||
        detail.request?.status === "DECLINED" ||
        detail.request?.status === "REJECTED"
      ) {
        return {
          success: false,
          error: "Sorry, the request has been cancelled and removed from queue",
        };
      } else if (detail.request?.status === "USER DECLINED ATTESTATION") {
        return {
          success: false,
          error: "Sorry, the attestation request has been declined by the user",
        };
      } else if (detail.request?.autoReject) {
        return {
          success: false,
          error: "Sorry, the request has been declined by the user",
        };
      } else if (detail.request?.isSendBack) {
        return {
          success: false,
          error: "Sorry, you do not have access to view the request",
        };
      } else {
        success = await approveRequest([detail.request?.id || 0], comments);

        if (success) {
          history.push(`/dashboard/approve-request`);
          alertService.success({
            msg: "Request has been successfully approved.",
          });
        } else {
          alertService.danger({ msg: "Failed to approve." });
        }
      }

      return { success };
    },
    [detail.request]
  );

  const [isReSubmitting, setIsResubmitting] = useState(false);
  const [isAttesting, setIsAttesting] = useState(false);
  const [isOpenResubmitConfirmation, setIsOpenResubmitConfirmation] = useState(
    false
  );
  const [financeForm, setFinanceForm] = useState([]);
  const [resubmitData, setResubmitData] = useState<any>({});
  const [selectedFormName, setSelectedFormName] = useState("");
  const onResubmit = useCallback(
    async (data: any) => {
      setIsResubmitting(true);
      const dataToSubmit = {
        id: resubmitData.id,
        isSendBack: resubmitData.isSendBack,
        accessRequestJustification: resubmitData.accessRequestJustification,
        formName: selectedFormName,
        justification: resubmitData.justification,
        formId: detail.request.accessRequests[0].formId_,
        formFinanceSecurities:
          financeForm.length == 0
            ? detail.request.accessRequests[0].additionalForm?.formFinance
                ?.securities
            : financeForm,
      };
      resubmitMyRequest(dataToSubmit)
        .then((response) => {
          if (response) {
            alertService.success({
              msg: "Request has been successfully resubmitted.",
            });
            history.push("/dashboard/my-request");
          } else {
            alertService.danger({ msg: "Failed to resubmit" });
          }
        })
        .catch((error) => {
          if (error.response) {
            alertService.danger({ statusCode: error.response.status });
          }
        })
        .finally(() => {
          setIsResubmitting(false);
        });
    },
    [detail.request, resubmitData, financeForm]
  );

  const getReSubmitRequestData = (data: any) => {
    setResubmitData(data);
  };

  const financeFormSecuritiesForResubmit = (data: any) => {
    if (data.length !== 0) {
      setSelectedFormName("financeForm");
      setFinanceForm(data);
    }
  };

  let resubmitText = isReSubmitting ? "Resubmitting..." : "Re-submit";

  let content = null;
  if (!!detail.request) {
    content = (
      <Grid container direction="row" spacing={2}>
        <Grid item xs={12}>
          <Box className={classNames.link}>
            <Typography
              variant="body1"
              className={classNames.link}
              onClick={() => history.goBack()}
            >
              &larr; Back to List
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box display="flex" alignItems="center" color="textSecondary">
            <Box>
              <Typography variant="body1" color="textSecondary">
                <FontAwesomeIcon icon={faFileAlt} />
              </Typography>
            </Box>
            <Box pl={1}>
              <Typography variant="body1" color="textSecondary">
                Request {detail.request?.referenceNumber}
              </Typography>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <RequestorDetails
            requestor={{
              name: detail.request.requestor,
              location: detail.request.requestorOffice,
              phoneNumber: detail.request.requestorTelephoneNumber,
              email: detail.request.requestorEmail,
            }}
          ></RequestorDetails>
        </Grid>
        <Grid item xs={12}>
          <RequestDetails
            reSubmitRequestData={getReSubmitRequestData}
            request={detail.request}
            financeFormSecuritiesForResubmit={financeFormSecuritiesForResubmit}
          ></RequestDetails>
        </Grid>
        <Grid item xs={12}>
          <ApproversDetails
            approvers={detail.request?.approvers[0]}
          ></ApproversDetails>
        </Grid>
        <Grid item xs={12}>
          <Buttons
            detail={detail}
            onResubmitRequest={() => setIsOpenResubmitConfirmation(true)}
            onViewHistory={() => setOpenHistory(true)}
            onSendBack={() => setOpenSendBack(true)}
            onReject={() => setOpenReject(true)}
            onApproveRequest={() => setOpenApprove(true)}
            isReSubmitting={isReSubmitting}
            onViewAttestation={() => {
              if (
                (location.state?.readonlyAttestation &&
                  detail.viewRole !== "Approver") ||
                detail.request.isAttestationSigned ||
                detail.request.requestorEmail === user?.email ||
                (detail.viewRole === "Requestor" &&
                  detail.request.requestorEmail !== user?.email)
              ) {
                setOpenAttestation(true);
              } else {
                history.push(`${location.pathname}/accept-attestation`);
              }
            }}
            onCancel={() => setOpenCancel(true)}
            prevPage={location.state?.prevPage}
          ></Buttons>
        </Grid>

        <ComponentModal
          isOpen={isOpenResubmitConfirmation}
          closeModal={() => setIsOpenResubmitConfirmation(false)}
        >
          <Typography align="center" paragraph>
            Are you sure you want to resubmit this request?
          </Typography>
          <Box textAlign="right" mt={4}>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setIsOpenResubmitConfirmation(false)}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="button"
              onClick={onResubmit}
              style={{ marginLeft: "40px" }}
              disabled={isReSubmitting}
            >
              {resubmitText}
            </Button>
          </Box>
        </ComponentModal>
        <ModalHistoryUserRequest
          isOpen={openHistory}
          closeModal={() => setOpenHistory(false)}
          headerId={detail.request.id}
        />
        <Modal
          isOpen={openSendBack}
          title="Send Back Request"
          closeModal={() => {
            setOpenSendBack(false);
          }}
          buttonText="SEND BACK REQUEST"
          successMessage="Successfully Sent Back"
          onAction={onSendBack}
          required={true}
        ></Modal>
        <Modal
          isOpen={openApprove}
          closeModal={() => {
            setOpenApprove(false);
            history.goBack();
          }}
          buttonText="APPROVE REQUEST"
          successMessage="Successfully Approved"
          onAction={onApproveRequest}
        ></Modal>
        <Modal
          isOpen={openReject}
          title="Reject Request"
          closeModal={() => {
            setOpenReject(false);
          }}
          buttonText="REJECT REQUEST"
          successMessage="Successfully Rejected"
          onAction={onReject}
          required={true}
        ></Modal>
        <Modal
          isOpen={openCancel}
          title="Cancel Request"
          closeModal={() => {
            setOpenCancel(false);
          }}
          buttonText="CANCEL REQUEST"
          successMessage="Successfully Cancelled"
          onAction={onCancel}
          required={false}
        ></Modal>
        <AttestationModal
          error={attestationError}
          mobile={isMobileAttestation}
          name={current_user_name}
          selectedUserForTheRequest={detail.request.userName || ""}
          open={openAttestation}
          onAccept={() => onAttestationAccept(true)}
          onChange={(name: "attestation", value: string) =>
            setAttestation(value)
          }
          onDecline={() => onAttestationAccept(false)}
          onClose={onCloseAttestationModal}
          isAttesting={isAttesting}
          readonly={
            location.state?.readonlyAttestation ||
            detail.request.isAttestationSigned ||
            detail.request.requestorEmail === user?.email ||
            detail.viewRole === "Requestor"
          }
          status={
            detail.request?.status === "USER DECLINED ATTESTATION"
              ? "declined"
              : detail.request.isAttestationSigned
              ? "accepted"
              : ""
          }
          value={attestation}
        />
      </Grid>
    );
  }
  return content;
};
